import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  async function loginSession(e: React.FormEvent) {
    e.preventDefault();
    setMessage(null);
    try {
      const res = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        credentials: 'include', // Include cookies for session
      });
      if (!res.ok) throw new Error('Invalid credentials');
      const data = await res.json();
      // If user must reset password, redirect to reset page
      if (data.mustResetPassword) {
        navigate('/reset-password?forced=true');
      } else {
        // Check if user should be redirected to long-term listings
        // This happens if they only have access to long-term listings (all other nav items disabled)
        const navPerms = data.navigationPermissions;
        if (navPerms && !data.roles?.includes('admin') && !data.roles?.includes('superadmin')) {
          const hasOnlyLongTerm = navPerms.longTermListings && 
            !navPerms.browse && 
            !navPerms.campaigns && 
            !navPerms.reservations && 
            !navPerms.export;
          if (hasOnlyLongTerm) {
            navigate('/new-location-tender');
            return;
          }
        }
        navigate('/');
      }
    } catch (err: any) {
      setMessage(err.message || 'Login failed');
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Dark Blue Background with Logo */}
      <div 
        className="hidden md:flex md:w-2/3 bg-[#0D227A] items-center justify-center"
      >
        <img 
          src="/images/bb_primary logo (outlined).svg" 
          alt="Bright Blue Logo" 
          className="h-32 w-auto"
        />
      </div>

      {/* Right Side - White Background with Login Form */}
      <div className="w-full md:w-1/3 bg-white flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Login Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
            <h1 className="text-2xl font-bold mb-6 text-black text-center">Log in</h1>
            <form className="space-y-5" onSubmit={loginSession}>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
                <Input 
                  id="email" 
                  type="email" 
                  value={email} 
                  onChange={e => setEmail(e.target.value)} 
                  required 
                  className="w-full border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">Password</Label>
                <div className="relative">
                  <Input 
                    id="password" 
                    type={showPassword ? 'text' : 'password'} 
                    value={password} 
                    onChange={e => setPassword(e.target.value)} 
                    required 
                    className="w-full border-gray-300 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#0047FF] hover:bg-[#0039CC] text-white py-6 text-base font-semibold"
              >
                Login
              </Button>
            </form>
            {message && <p className="text-sm text-red-600 mt-4">{message}</p>}
          </div>

          {/* Powered by Section */}
          <div className="flex items-center justify-center gap-3">
            <p className="text-sm text-gray-600">Powered by</p>
            <img 
              src="/images/bb_primary logo (dark).svg" 
              alt="Bright Blue Logo" 
              className="h-8 w-auto"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
